import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import folderIconSrc from '../../assets/icons/FolderIcon.png';
import helpIconSrc from '../../assets/icons/HelpIcon.png';
import scaleIconSrc from '../../assets/icons/ScaleIcon.png';
import { useAppState } from '../../context/AppState/useAppState';
import { useModal } from '../../context/Modal/useModal';
import { useUser } from '../../context/User/useUser';
import { useRoomId } from '../../hooks/useRoomId';
import Button, { ButtonType } from '../Button/Button';
import { ExportButton } from '../Export/Export';
import {
  CloudExlamIcon,
  CloudIcon,
  CloudSyncarrowIcon,
  RedoIcon,
  UndoIcon,
} from '../Icons';
import { QuickHelModal } from '../QuickHelp/QuickHelp';
import { Settings } from './Settings';

const pulseOpacity = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.72; }
`;

const CanvasName = styled.span`
  font-size: 0.9rem;
  color: var(--text);
  white-space: nowrap;
`;

const SyncStatusWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  color: var(--text-muted);
  gap: 6px;
  font-size: 0.75rem;
  overflow: hidden;
  min-width: 200px;
  max-width: 100%;
`;

const SyncIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const SyncText = styled.span`
  white-space: nowrap;
`;

const PulsingCloud = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  animation: ${pulseOpacity} 1.5s ease-in-out infinite;
`;

function SyncStatus({ pendingCount }: { pendingCount: number }) {
  const [stableCount, setStableCount] = useState(pendingCount);
  const [isStalled, setIsStalled] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (pendingCount === 0) {
      const timer = setTimeout(() => setStableCount(0), 1500);
      return () => clearTimeout(timer);
    }
    setStableCount(pendingCount);
  }, [pendingCount]);

  useEffect(() => {
    if (pendingCount === 0) {
      setIsStalled(false);
      return;
    }
    const timer = setTimeout(() => setIsStalled(true), 30_000);
    return () => clearTimeout(timer);
  }, [pendingCount]);

  if (stableCount === 0) {
    return (
      <SyncStatusWrapper>
        <SyncIcon>
          <CloudIcon fill={'var(--mid-blue)'} />
        </SyncIcon>
        <SyncText>{intl.formatMessage({ id: 'topMenu.syncSaved' })}</SyncText>
      </SyncStatusWrapper>
    );
  }
  if (isStalled) {
    return (
      <SyncStatusWrapper>
        <SyncIcon>
          <CloudExlamIcon fill={'var(--status-red-foreground)'} />
        </SyncIcon>
        <SyncText>{intl.formatMessage({ id: 'topMenu.syncStalled' })}</SyncText>
      </SyncStatusWrapper>
    );
  }
  if (stableCount <= 5) {
    return (
      <SyncStatusWrapper>
        <PulsingCloud>
          <CloudSyncarrowIcon fill={'var(--mid-blue)'} />
        </PulsingCloud>
        <SyncText>{intl.formatMessage({ id: 'topMenu.syncSaving' })}</SyncText>
      </SyncStatusWrapper>
    );
  }
  return (
    <SyncStatusWrapper>
      <SyncIcon>
        <CloudExlamIcon fill={'var(--status-red-foreground)'} />
      </SyncIcon>
      <SyncText>
        {intl.formatMessage(
          { id: 'topMenu.syncUnsaved' },
          { count: stableCount }
        )}
      </SyncText>
    </SyncStatusWrapper>
  );
}

const Bar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  box-sizing: border-box;
  min-height: 50px;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem;
  border-bottom: 1px solid var(--border);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  z-index: 2;
  background: var(--surface);
`;

const UndoButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0rem;
  color: var(--text);
  border: solid 1px var(--border);
  background-color: var(--surface-elevated);
  border-radius: 2px;
  max-height: 32px;
  box-shadow:
    0px 1px 6px 0 rgba(0, 0, 0, 0.06),
    0px 1px 0 0 var(--border-light);

  font-size: 0.9rem;
  padding: 0;
  &:hover {
    border: solid 1px var(--border-dark);
  }
`;

const UndoRedoButton = styled.button`
  border: none;
  background-color: var(--surface-elevated);
  cursor: pointer;
  padding: 0.5rem;
  margin: 0;

  &:hover {
    background-color: #fff;
  }

  &:disabled {
    opacity: 0.3;
    background-color: var(--surface);
    cursor: not-allowed;
  }
`;

const IconImg = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const Divider = styled.div`
  width: 1px;
  background-color: var(--border);
  margin: 0.4rem 0;
`;

const BarItem = styled.div<{ $textAlign?: string }>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => props.$textAlign ?? 'center'};
  align-items: center;
  gap: 4px;
  text-align: ${(props) => props.$textAlign ?? 'left'};
  a {
    color: red;
    text-decoration: none;
    vertical-align: middle;
  }

  a:hover {
    color: var(--active-blue);
  }

  a:hover > svg {
    fill: var(--active-blue);
  }

  a small {
    margin-right: 0.25rem;
  }
`;

function TopMenu() {
  const { onOpen } = useModal();
  const user = useUser();
  const canvasId = useRoomId();
  const intl = useIntl();
  const canvas = user.canvases.find((c) => c.canvasId === canvasId);
  const openMapModal = () => {
    onOpen(<Settings canvasId={canvasId} />);
  };

  const { commandManager, pendingCount } = useAppState();

  return (
    <Bar id="top-menu">
      <BarItem id="top-menu" $textAlign="flex-start">
        <Link to="/projects">
          <Button type={ButtonType.Secondary}>
            {' '}
            <IconImg src={folderIconSrc} alt="Menu" />
          </Button>
        </Link>
        <Button
          type={ButtonType.Secondary}
          onClick={() => onOpen(<QuickHelModal />)}
        >
          <IconImg src={helpIconSrc} alt="Quick Help" />
        </Button>
        <Button type={ButtonType.Secondary} onClick={openMapModal}>
          {' '}
          <IconImg src={scaleIconSrc} alt="Settings" />
          {intl.formatMessage({ id: 'topMenu.mapAndScale' })}{' '}
        </Button>
        <UndoButtonWrapper>
          <UndoRedoButton
            disabled={!commandManager.canUndo()}
            onClick={() => commandManager.undo()}
          >
            <UndoIcon />
          </UndoRedoButton>
          <Divider />
          <UndoRedoButton
            disabled={!commandManager.canRedo()}
            onClick={() => commandManager.redo()}
          >
            <RedoIcon />
          </UndoRedoButton>
        </UndoButtonWrapper>
      </BarItem>
      <BarItem $textAlign="center" id="top-menu">
        <CanvasName>{canvas?.name}</CanvasName>
      </BarItem>
      <BarItem $textAlign="right" id="top-menu">
        <SyncStatus pendingCount={pendingCount} />
        <ExportButton />
      </BarItem>
    </Bar>
  );
}

export default TopMenu;
